/**
 * useReportData Hook (Story 5.11, Task 4)
 *
 * Fetches real integration data for report widgets via POST /api/reports/populate.
 * Returns per-widget data envelopes and per-platform status metadata.
 *
 * Used in the preview step to replace mock data with live integration data.
 * Maintains the same Record<string, WidgetData> contract as generateMockData().
 */

import { useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { apiPost } from "../lib/apiClient";
import type { WidgetConfig } from "../lib/templateUtils";
import type { WidgetData } from "../lib/widgetData";
import { createLoadingEnvelope, createErrorEnvelope } from "../lib/widgetData";
import { deriveStaleTimeMs } from "../lib/reportStaleTime";

// ============================================================================
// Types
// ============================================================================

export interface PlatformStatus {
  status: "success" | "error" | "missing";
  fromCache: boolean;
  stale: boolean;
  cachedAt: string | null;
  error?: string;
}

export interface PopulateResponse {
  widgetData: Record<string, WidgetData>;
  platformStatus: Record<string, PlatformStatus>;
}

export interface UseReportDataResult {
  /** Per-widget data envelopes keyed by widget ID */
  widgetData: Record<string, WidgetData>;
  /** Per-platform fetch status metadata */
  platformStatus: Record<string, PlatformStatus>;
  /**
   * True only on the FIRST load (no data/placeholder yet). On a date-range /
   * comparison key change, `keepPreviousData` holds prior data so `isLoading`
   * is `false` — read `isUpdating` for the "refetching" affordance instead.
   */
  isLoading: boolean;
  /**
   * Background fetch in flight (initial OR refetch). With `keepPreviousData`,
   * `isFetching && isPlaceholderData` means a key-change refetch is running
   * while prior numbers stay on screen (Story 37.8, ADR-RE-7 layer 3).
   */
  isFetching: boolean;
  /** True while the displayed data is the PREVIOUS query's data (placeholder). */
  isPlaceholderData: boolean;
  /**
   * Convenience flag for the "updating…" affordance: a key-change refetch is
   * running while prior data is held (`isFetching && isPlaceholderData`).
   * `isLoading` alone is insufficient — with `keepPreviousData` the query
   * reports `isLoading: false` on a key change (correction #6a, Story 37.8).
   */
  isUpdating: boolean;
  /**
   * Overall error state. NOTE: with `placeholderData: keepPreviousData` the
   * query status stays `"success"` while prior data is held, so a FAILED
   * REFETCH does NOT flip `isError` — it only fires on the FIRST load (no
   * placeholder). For refetch-failure surfacing read
   * `platformStatus[...].status === "error"` instead (correction #6a).
   */
  isError: boolean;
  /** Error object if request failed */
  error: Error | null;
  /** Whether any platform returned stale comparison data */
  hasStaleComparison: boolean;
  /** Refetch all platform data */
  refetch: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Deterministic, throw-safe JSON stringify for the widget-binding signature
 * (Story 37.8 review R2). Object keys are emitted in SORTED order at every depth
 * so two structurally-equal configs with different key insertion order produce
 * the SAME string (no spurious re-key → no spurious refetch). Returns `null`
 * (JSON) on any failure — `config` is `Record<string, unknown>` and could in
 * principle hold a circular or non-serializable value; a thrown `JSON.stringify`
 * inside the query-key `useMemo` would otherwise crash the editor. Caller folds
 * the per-widget `id`/`type` in alongside, so a degraded `null` config still
 * keys distinctly per widget id.
 */
export function stableStringify(value: unknown): string {
  // Cycle detection tracks the CURRENT DFS PATH (ancestors), not a global
  // visited-set — so only a TRUE circular reference (an object that is its own
  // ancestor) degrades to `null`. A merely-SHARED reference that appears twice
  // in sibling branches must serialize identically both times (review R3): a
  // path-insensitive `WeakSet` wrongly nulled the 2nd occurrence, so
  // `{a:shared, b:shared}` and `{a:{x},b:{x}}` produced DIFFERENT signatures —
  // spurious re-keys, and worse, a data-affecting field behind a shared ref
  // could be dropped to `null` and a real binding change would NOT re-key.
  const path = new Set<object>();
  const norm = (v: unknown): unknown => {
    if (v === null || typeof v !== "object") return v;
    if (path.has(v as object)) return null; // ancestor cycle → null (degrade, don't throw)
    path.add(v as object);
    let result: unknown;
    if (Array.isArray(v)) {
      result = v.map(norm);
    } else {
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(v as Record<string, unknown>).sort()) {
        out[key] = norm((v as Record<string, unknown>)[key]);
      }
      result = out;
    }
    path.delete(v as object); // leaving this node — siblings may reuse the ref
    return result;
  };
  try {
    return JSON.stringify(norm(value)) ?? "null";
  } catch {
    return "null";
  }
}

// ============================================================================
// Hook
// ============================================================================

export function useReportData(
  clientId: string | undefined,
  widgets: WidgetConfig[],
  dateRange: { start: string; end: string },
  comparison?: { start: string; end: string } | null,
  options?: { forceRefresh?: boolean }
): UseReportDataResult {
  const queryClient = useQueryClient();

  // Stable query key that auto-refetches when date range or comparison changes
  const comparisonKey = comparison
    ? `${comparison.start}_${comparison.end}`
    : "none";

  // Story 37.8 (review R1): the response depends on the WIDGET SET's data
  // bindings (each widget's id + type + config drive what the server fetches),
  // so a signature of those MUST be in the key. Without it, adding/removing a
  // widget — or changing its metric/platform — keeps the SAME key, and the
  // raised `staleTime` (layer 4) serves the OLD cached payload as fresh: the new
  // widget renders with no data until the date range changes. Title/position are
  // excluded (they don't affect the populate response).
  //
  // Review R2: the signature is built with a STABLE-KEY-ORDER stringify
  // (`stableStringify`) so reserializing an identical config (different key
  // insertion order) does NOT re-key, and is THROW-SAFE so a non-serializable /
  // circular `config` field (config is `Record<string, unknown>`) can never
  // crash the editor — it degrades to an `id:type` token. The whole `config` is
  // hashed (not a hand-picked subset): correctness wins over quota — an
  // under-projection that missed a data-affecting field (formulaExpression,
  // aggregateKey, sentinel metricKeys, dimension, …) would serve STALE data,
  // which is far worse than the bounded cost of an occasional refetch on a
  // cosmetic edit (already debounced + staleTime-gated). The per-widget tokens
  // are sorted so a pure REORDER does not refetch.
  const widgetsSignature = useMemo(
    () =>
      widgets
        .map((w) => stableStringify({ id: w.id, type: w.type, config: w.config }))
        .sort()
        .join("|"),
    [widgets]
  );

  const queryKey = useMemo(
    () => [
      "report-data",
      clientId,
      dateRange.start,
      dateRange.end,
      comparisonKey,
      widgetsSignature,
    ],
    [clientId, dateRange.start, dateRange.end, comparisonKey, widgetsSignature]
  );

  // ADR-RE-7 layer 4 — TTL-aligned freshness (Story 37.8). `staleTime` = MIN
  // platform soft-TTL across the widget set (ms), so revisiting a date range
  // within its soft-TTL window serves from cache and issues NO network request.
  // Capped at the soft TTL inside `deriveStaleTimeMs`, never the 7-day hard
  // expiry (which would mask legitimately-refreshed server data).
  const staleTime = useMemo(() => deriveStaleTimeMs(widgets), [widgets]);

  // `gcTime` (how long an INACTIVE range's cache survives in memory) = `staleTime`
  // per correction #11 — the library default (5 min) is shorter than every soft
  // TTL, so without this an inactive range's cache is GC'd the moment the user
  // lingers on another range >5 min, and the within-soft-TTL revisit (AC #4)
  // would refetch despite being fresh. `gcTime` MUST be ≥ `staleTime` so AC #4
  // holds for ALL platforms (incl. GSC 4h / Moz 24h / PageSpeed 7d), not just
  // GA4. (Review R2: a 30-min cap tried here regressed AC #4 for every platform
  // with a soft TTL > 30 min — reverted.) Memory is bounded in practice: RQ only
  // retains INACTIVE entries with no observer, and a session visits ~10 distinct
  // ranges (the ADR-RE-7 quota math), so retention is small even at the 7-day
  // ceiling. The whole-report key already scopes entries to (client, range,
  // comparison, widget-set).
  const gcTime = staleTime;

  // ── Quota-safety math (ADR-RE-7 net effect: ~200 live calls → ~10) ─────────
  // A session scrubbing ~20 date ranges on a 5-platform report would naively
  // fire ~200 live API calls (20 ranges × 5 platforms × per-keystroke). Four
  // client-side layers over the EXISTING server infra collapse that to ~10
  // cache-priming fetches — NO new endpoint, cache, or rate limiter:
  //   1. Debounce (editor wiring): scrubbing N intermediate ranges → ≤1 fetch
  //      per LANDED range. 20 scrubs across ~10 distinct landed ranges → ~10.
  //   2. Server per-platform batch (widget-populate.ts, already): N widgets on
  //      one platform → 1 platform fetch, inside ONE whole-report call. The
  //      5-platform fan-out is the server's job, not N client queries.
  //   3. keepPreviousData (below): zero-flicker refetch — no extra calls, UX.
  //   4. staleTime (above): a revisited range within its soft TTL = 0 network.
  // Net: ~10 distinct landed ranges → ~10 priming fetches, repeats are free.
  const {
    data,
    isLoading,
    isError,
    error,
    isFetching,
    isPlaceholderData,
  } = useQuery<PopulateResponse>({
    queryKey,
    // ADR-RE-7 layer 3 — keep prior numbers visible during a key-change refetch
    // (no blank-then-populate flash). v5 FUNCTION import, not the removed boolean.
    placeholderData: keepPreviousData,
    staleTime,
    gcTime,
    queryFn: async () => {
      const body: Record<string, unknown> = {
        clientId,
        widgets: widgets.map((w) => ({
          id: w.id,
          type: w.type,
          title: w.title,
          position: w.position,
          config: w.config,
          ...(w.autoSize !== undefined ? { autoSize: w.autoSize } : {}),
        })),
        dateRange: { start: dateRange.start, end: dateRange.end },
      };

      if (comparison) {
        body.comparison = {
          start: comparison.start,
          end: comparison.end,
        };
      }

      if (options?.forceRefresh) {
        body.forceRefresh = true;
      }

      const res = await apiPost("/api/reports/populate", body);

      if (!res.ok) {
        const text = await res.text().catch(() => "Unknown error");
        throw new Error(
          `Failed to populate report data (${res.status}): ${text}`
        );
      }

      const json = await res.json();
      return json.data as PopulateResponse;
    },
    // Guard the DATES too (review R2): the server requires a real ISO range, and
    // an editor render before the date range is set (or a report hydrated with
    // empty start/end) would otherwise fire a wasted populate with `{start:"",
    // end:""}` — the exact quota cost this story exists to avoid.
    enabled: !!clientId && widgets.length > 0 && !!dateRange.start && !!dateRange.end,
  });

  // Build per-widget envelopes based on query state
  const widgetData = useMemo(() => {
    if (isError) {
      const errData: Record<string, WidgetData> = {};
      for (const widget of widgets) {
        errData[widget.id] = createErrorEnvelope<
          import("../lib/widgetData").AnyWidgetPayload
        >(error?.message ?? "Failed to load report data");
      }
      return errData;
    }

    if (isLoading || !data) {
      const loading: Record<string, WidgetData> = {};
      for (const widget of widgets) {
        loading[widget.id] = createLoadingEnvelope<
          import("../lib/widgetData").AnyWidgetPayload
        >();
      }
      return loading;
    }

    return data.widgetData;
  }, [data, isLoading, isError, error, widgets]);

  const platformStatus = data?.platformStatus ?? {};

  // Detect stale comparison data (AC #5)
  const hasStaleComparison = useMemo(() => {
    if (!comparison) return false;
    return Object.values(platformStatus).some(
      (ps) => ps.status === "success" && ps.stale
    );
  }, [platformStatus, comparison]);

  const refetch = useCallback(() => {
    queryClient.invalidateQueries({ queryKey });
  }, [queryClient, queryKey]);

  // "updating…" affordance flag: a key-change refetch is running while prior
  // data is held. `isLoading` is false in that state (keepPreviousData), so the
  // editor must read THIS, not `isLoading` (correction #6a, Story 37.8).
  const isUpdating = isFetching && isPlaceholderData;

  return {
    widgetData,
    platformStatus,
    isLoading,
    isFetching,
    isPlaceholderData,
    isUpdating,
    isError,
    error: error as Error | null,
    hasStaleComparison,
    refetch,
  };
}
